import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { company } from "@/data/company";

const Privacy = () => {
  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute("content") ?? "";

    document.title = "Privacybeleid | LX Bouw";
    meta?.setAttribute(
      "content",
      "Lees hoe LX Bouw persoonsgegevens uit het contactformulier gebruikt, beveiligt en bewaart."
    );

    return () => {
      document.title = previousTitle;
      meta?.setAttribute("content", previousDescription);
    };
  }, []);

  return (
    <div className="bg-white text-ink">
      <Navbar />
      <main>
        <section className="bg-offwhite pt-40 pb-20 md:pt-48 md:pb-24">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
            <p className="font-body text-xs tracking-[0.25em] uppercase text-forest mb-6">
              Privacy
            </p>
            <h1 className="font-heading font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-balance">
              Privacybeleid
            </h1>
            <p className="mt-6 text-slate2">Laatst gewijzigd op 13 juli 2026.</p>
          </div>
        </section>

        <section className="py-20 md:py-28 lg:py-32">
          <article className="mx-auto max-w-3xl px-6 md:px-12 font-body text-slate2 leading-relaxed">
            <p className="text-lg md:text-xl font-light leading-relaxed text-ink">
              LX Bouw respecteert je privacy. In dit beleid leggen we helder uit welke
              gegevens we via het contactformulier verwerken, waarom we dat doen en welke
              rechten je daarbij hebt.
            </p>

            <PrivacySection title="1. Wie is verantwoordelijk voor je gegevens?">
              <p>De verwerkingsverantwoordelijke voor het contactformulier is:</p>
              <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 rounded-2xl bg-offwhite p-6 text-sm md:text-base">
                <dt className="font-medium text-ink">Bedrijfsnaam</dt><dd>{company.name}</dd>
                <dt className="font-medium text-ink">Ondernemingsnummer</dt><dd>BE1035.585.361</dd>
                <dt className="font-medium text-ink">Adres</dt><dd>{company.street}, {company.city}</dd>
                <dt className="font-medium text-ink">Telefoon</dt><dd><a className="privacy-link" href={`tel:${company.phoneRaw}`}>{company.phone}</a></dd>
                <dt className="font-medium text-ink">E-mail</dt><dd><a className="privacy-link" href={`mailto:${company.email}`}>{company.email}</a></dd>
              </dl>
            </PrivacySection>

            <PrivacySection title="2. Welke gegevens verwerken we?">
              <p>Wanneer je het contactformulier gebruikt, verwerken we:</p>
              <ul className="privacy-list">
                <li>je naam;</li>
                <li>je e-mailadres;</li>
                <li>je telefoonnummer;</li>
                <li>de inhoud van je bericht;</li>
                <li>technisch noodzakelijke beveiligingsgegevens, zoals je IP-adres of een daarvan afgeleide hash, wanneer die nodig zijn voor rate limiting, fraudepreventie of serverlogs.</li>
              </ul>
              <p>De verplichte velden zijn nodig om je aanvraag correct te kunnen beoordelen en beantwoorden.</p>
            </PrivacySection>

            <PrivacySection title="3. Waarom verwerken we deze gegevens?">
              <p>We gebruiken deze gegevens uitsluitend om:</p>
              <ul className="privacy-list">
                <li>contact met je op te nemen en vragen over LX Bouw of een specifiek bouwproject te beantwoorden;</li>
                <li>een concrete commerciële of precontractuele aanvraag op te volgen;</li>
                <li>misbruik, geautomatiseerde inzendingen en spam te voorkomen.</li>
              </ul>
              <p>We gebruiken de gegevens niet voor nieuwsbrieven, reclame of profilering en verkopen ze niet aan derden.</p>
            </PrivacySection>

            <PrivacySection title="4. Op welke rechtsgrond steunt de verwerking?">
              <p>
                We verwerken je contactgegevens omdat dit nodig is om op jouw verzoek
                stappen te nemen vóór het eventueel sluiten van een overeenkomst. Waar
                passend steunen we op ons gerechtvaardigd belang om concrete aanvragen te
                beantwoorden en onze website tegen misbruik en spam te beschermen.
              </p>
              <p className="mt-4">
                De checkbox bij het formulier bevestigt dat je deze informatie hebt gelezen.
                Ze is geen toestemming voor marketing en verandert de hierboven beschreven
                rechtsgronden niet.
              </p>
            </PrivacySection>

            <PrivacySection title="5. Wie kan de gegevens ontvangen?">
              <p>Je gegevens zijn alleen toegankelijk voor:</p>
              <ul className="privacy-list">
                <li>bevoegde medewerkers van LX Bouw die je aanvraag opvolgen;</li>
                <li>onze hosting- en e-mailproviders, voor zover dit technisch noodzakelijk is om het formulier te verwerken en e-mail af te leveren.</li>
              </ul>
              <p>We verkopen je persoonsgegevens niet en delen ze niet voor de marketingdoeleinden van andere partijen.</p>
            </PrivacySection>

            <PrivacySection title="6. Hoelang bewaren we de gegevens?">
              <p>
                We bewaren je gegevens zolang dit nodig is om je aanvraag en het relevante
                project op te volgen. Ze worden uiterlijk verwijderd nadat het betrokken
                project verkocht of afgesloten is, tenzij een langere wettelijke
                bewaarplicht geldt.
              </p>
              <p className="mt-4">
                We verwijderen de gegevens eerder wanneer je daar geldig om vraagt, voor
                zover geen wettelijke bewaarplicht of andere geldige grond verdere bewaring
                vereist.
              </p>
            </PrivacySection>

            <PrivacySection title="7. Welke rechten heb je?">
              <p>Afhankelijk van de verwerking heb je het recht om:</p>
              <ul className="privacy-list">
                <li>inzage te vragen in je persoonsgegevens;</li>
                <li>onjuiste of onvolledige gegevens te laten verbeteren;</li>
                <li>verwijdering of beperking van de verwerking te vragen;</li>
                <li>bezwaar te maken wanneer de verwerking op een gerechtvaardigd belang steunt;</li>
                <li>je gegevens te laten overdragen wanneer het recht op overdraagbaarheid van toepassing is.</li>
              </ul>
              <p>
                Stuur je verzoek naar <a className="privacy-link" href={`mailto:${company.email}`}>{company.email}</a> en vermeld voldoende informatie om je aanvraag terug te vinden. We kunnen om een passende identiteitscontrole vragen wanneer dat nodig is om je gegevens te beschermen.
              </p>
              <p className="mt-4">
                Je kunt ook een klacht indienen bij de Belgische{" "}
                <a
                  className="privacy-link"
                  href="https://www.gegevensbeschermingsautoriteit.be/burger/acties/klacht-indienen"
                  target="_blank"
                  rel="noreferrer"
                >
                  Gegevensbeschermingsautoriteit
                </a>.
              </p>
            </PrivacySection>

            <PrivacySection title="8. Geautomatiseerde beslissingen">
              <p>We gebruiken de gegevens uit het contactformulier niet voor geautomatiseerde besluitvorming of profilering.</p>
            </PrivacySection>

            <PrivacySection title="9. Privacyvragen">
              <p>
                Heb je een vraag over dit beleid of over de verwerking van je gegevens?
                Neem contact op via <a className="privacy-link" href={`mailto:${company.email}`}>{company.email}</a>, bel naar <a className="privacy-link" href={`tel:${company.phoneRaw}`}>{company.phone}</a> of schrijf naar {company.street}, {company.city}.
              </p>
            </PrivacySection>
          </article>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const PrivacySection = ({ title, children }) => (
  <section className="mt-12 border-t border-ink/10 pt-8 first:mt-0">
    <h2 className="font-heading text-2xl md:text-3xl font-light leading-tight text-ink text-balance mb-5">
      {title}
    </h2>
    {children}
  </section>
);

export default Privacy;
